import { IPersonalInfoCMS } from '@vfit/consumer/data-access';
import { IConfigDocumentCms } from '@vfit/shared/models';
import { ISkeleton } from '@vfit/shared/components';
import { purify } from '@vfit/shared/themes';

export const DEFAULT_CUSTOMER_REFERENCE = {
  nameTitle: [
    {
      rel: 'self',
      href: '/care/v1/nameTitle',
      id: 'EMPTY',
      name: 'EMPTY',
      displayName: ' ',
      default: true,
    },
    {
      rel: 'self',
      href: '/care/v1/nameTitle',
      id: 'Mr.',
      name: 'Mr.',
      displayName: 'Sig.',
      default: false,
    },
    {
      rel: 'self',
      href: '/care/v1/nameTitle',
      id: 'Mrs.',
      name: 'Mrs.',
      displayName: 'Sig.ra',
      default: false,
    },
    {
      rel: 'self',
      href: '/care/v1/nameTitle',
      id: 'Ms.',
      name: 'Ms.',
      displayName: 'Sig.na',
      default: false,
    },
    {
      rel: 'self',
      href: '/care/v1/nameTitle',
      id: 'Dr.',
      name: 'Dr.',
      displayName: 'Dr.',
      default: false,
    },
  ],
  identificationType: [
    {
      rel: 'self',
      href: '/care/v1/identificationType',
      id: '1',
      name: '1',
      displayName: "Carta d'identità",
      default: false,
    },
    {
      rel: 'self',
      href: '/care/v1/identificationType',
      id: '2',
      name: '2',
      displayName: 'Passaporto',
      default: false,
    },
    {
      rel: 'self',
      href: '/care/v1/identificationType',
      id: '3',
      name: '3',
      displayName: 'Patente di guida',
      default: false,
    },
  ],
};

const getValidationByID = (editorialConfigs, id: string) => {
  const parser = new DOMParser();
  const value = editorialConfigs?.find((e) => e.id === id)?.value || '';
  const doc = parser.parseFromString(purify([value || ''])?.[0] || '', 'text/html');
  return doc?.body?.textContent?.replace('\n', '') || '';
};

export const organizeDocument = (
  documentIdCardCMS: IPersonalInfoCMS,
  isRecognized: boolean
): IConfigDocumentCms => ({
  title: isRecognized
    ? documentIdCardCMS?.personalinfo?.preFilledDocument?.title || 'Documento'
    : documentIdCardCMS?.personalinfo?.newDocument?.title || 'Documento',
  subtitle: isRecognized
    ? documentIdCardCMS?.personalinfo?.preFilledDocument?.description ||
      "Conferma o modifica i dati del tuo documento. Per verificare la tua identità, dovrai mostrare lo stesso documento durante la consegna o l'attivazione online della tua nuova SIM."
    : documentIdCardCMS?.personalinfo?.newDocument?.description ||
      "Inserisci i dati di un tuo documento. Per verificare la tua identità, dovrai mostrare lo stesso documento durante la consegna o l'attivazione online della tua nuova SIM.",
  docLable: documentIdCardCMS?.personalinfo?.selectInput?.docType || '',
  docNumberLable: documentIdCardCMS?.personalinfo?.selectInput?.docNumber || '',
  docExpirationDateLable: documentIdCardCMS?.personalinfo?.selectInput?.docExpirationDate || '',
  countryLable: documentIdCardCMS?.personalinfo?.selectInput?.nation || '',
  errorMessage: isRecognized
    ? documentIdCardCMS?.personalinfo?.preFilledDocument?.allFieldsAreRequired ||
      'Tutti i campi sono obbligatori'
    : documentIdCardCMS?.personalinfo?.newDocument?.allFieldsAreRequired ||
      'Tutti i campi sono obbligatori',
  validationMessage: {
    identificationNumber: {
      valid:
        getValidationByID(
          documentIdCardCMS?.editorialconfigs?.elements,
          'identificationNumberValid'
        ) || 'Inserire un numero di documento valido',
      minimum:
        getValidationByID(documentIdCardCMS?.editorialconfigs?.elements, 'identificationMinimum') ||
        'Inserire un numero di documento valido',
    },
    otherwise: {
      valid:
        getValidationByID(documentIdCardCMS?.editorialconfigs?.elements, 'otherwiseValid') ||
        'Inserire un numero di documento valido',
      minimum:
        getValidationByID(documentIdCardCMS?.editorialconfigs?.elements, 'otherwiseMinimum') ||
        'Inserire un numero di documento valido',
      maximum:
        getValidationByID(documentIdCardCMS?.editorialconfigs?.elements, 'otherwiseMaximum') ||
        'Inserire un numero di documento valido',
    },
    expirationDate: {
      rangeTest:
        getValidationByID(
          documentIdCardCMS?.editorialconfigs?.elements,
          'expirationDateRangeTest'
        ) || 'Inserire una data di scadenza valida',
    },
  },
});

export const organizeIdentificationTypes = (
  identificationsType,
  editorialConfigs
): { displayName: string; identificationType: string; id: string }[] => {
  return identificationsType?.map((e) => {
    if (e?.id === '1' && getValidationByID(editorialConfigs, 'identityCard'))
      return {
        displayName: getValidationByID(editorialConfigs, 'identityCard') || e?.displayName || '',
        id: e?.id || '',
        identificationType: e?.displayName || '',
      };
    if (e?.id === '2' && getValidationByID(editorialConfigs, 'passport'))
      return {
        displayName: getValidationByID(editorialConfigs, 'passport') || e?.displayName || '',
        id: e?.id || '',
        identificationType: e?.displayName || '',
      };
    if (e?.id === '3' && getValidationByID(editorialConfigs, 'drivingLicense'))
      return {
        displayName: getValidationByID(editorialConfigs, 'drivingLicense') || e?.displayName || '',
        id: e?.id || '',
        identificationType: e?.displayName || '',
      };
    return {
      displayName: e?.displayName || '',
      id: e?.id || '',
      identificationType: e?.displayName || '',
    };
  });
};

export const SKELETON_SHAPE: ISkeleton = {
  margins: [60, 30],
  tabletMargins: [32, 15],
  groups: [
    { heights: 45, marginBottom: 15 },
    { heights: 60, marginBottom: 32 },
    { heights: 60, marginBottom: 15 },
    { heights: 75, repeat: 4, gap: 15 },
    { heights: 45, marginBottom: 15 },
  ],
};
